import { useState, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import {
  ArrowUpTrayIcon,
  DocumentArrowDownIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { importsApi } from '../../services/api';
import { hideCurrency } from '../../utils/format';
import { useValuesVisibility } from '../../stores/valuesVisibilityStore';

interface ParsedTransaction {
  date: string;
  type: 'BUY' | 'SELL';
  code: string;
  quantity: number;
  price: number;
  totalValue: number;
  broker: string;
}

type Step = 'upload' | 'preview' | 'done';

export default function ImportPage() {
  const { isVisible } = useValuesVisibility();
  const [step, setStep] = useState<Step>('upload');
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState<ParsedTransaction[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [result, setResult] = useState<{ imported: number; assetsCreated: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(async (file: File) => {
    setLoading(true);
    try {
      const data = await importsApi.preview(file);
      setTransactions(data.transactions);
      setSelected(new Set(data.transactions.map((_: unknown, i: number) => i)));
      setStep('preview');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg ?? 'Erro ao processar arquivo');
    } finally {
      setLoading(false);
    }
  }, []);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const toggleRow = (i: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };

  const handleConfirm = async () => {
    const toImport = transactions.filter((_, i) => selected.has(i));
    if (!toImport.length) { toast.error('Selecione ao menos uma transação'); return; }
    setLoading(true);
    try {
      const res = await importsApi.confirm(toImport);
      setResult(res);
      setStep('done');
      toast.success(`${res.imported} transações importadas com sucesso!`);
    } catch {
      toast.error('Erro ao confirmar importação');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const rows = [
      { Código: 'PETR4', Tipo: 'Compra', Data: '01/01/2025', Quantidade: 100, Preço: 35.50, Corretora: 'XP' },
      { Código: 'KNRI11', Tipo: 'Compra', Data: '15/01/2025', Quantidade: 10, Preço: 130.00, Corretora: 'Clear' },
      { Código: 'PETR4', Tipo: 'Venda', Data: '20/02/2025', Quantidade: 50, Preço: 38.00, Corretora: 'XP' },
    ];
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [10, 10, 12, 12, 10, 14].map((w) => ({ wch: w }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Transações');
    XLSX.writeFile(wb, 'template_investflow.xlsx');
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div>
        <h1 className="page-title">Importar Nota de Corretagem</h1>
        <p className="page-subtitle">Importe transações do CEI/B3 ou use nosso template</p>
      </div>

      {/* Steps indicator */}
      <div className="flex items-center gap-2 text-xs">
        {(['upload', 'preview', 'done'] as Step[]).map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`h-6 w-6 rounded-full flex items-center justify-center font-bold text-xs
              ${step === s ? 'bg-primary-500 text-white' : step === 'done' || (step === 'preview' && i === 0) ? 'bg-success-500/20 text-success-400' : 'bg-white/10 text-white/30'}`}>
              {i + 1}
            </div>
            <span className={step === s ? 'text-white' : 'text-white/30'}>
              {s === 'upload' ? 'Upload' : s === 'preview' ? 'Revisão' : 'Concluído'}
            </span>
            {i < 2 && <span className="text-white/20 mx-1">›</span>}
          </div>
        ))}
      </div>

      {/* STEP 1 — Upload */}
      {step === 'upload' && (
        <div className="space-y-4">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`card border-2 border-dashed cursor-pointer transition-colors flex flex-col items-center justify-center py-16 gap-4
              ${dragging ? 'border-primary-400 bg-primary-500/10' : 'border-white/10 hover:border-white/25'}`}
          >
            {loading ? (
              <ArrowPathIcon className="h-10 w-10 text-primary-400 animate-spin" />
            ) : (
              <ArrowUpTrayIcon className="h-10 w-10 text-white/30" />
            )}
            <div className="text-center">
              <p className="text-white font-medium">
                {loading ? 'Processando arquivo...' : 'Arraste o arquivo ou clique para selecionar'}
              </p>
              <p className="text-white/40 text-sm mt-1">Excel (.xlsx, .xls) ou CSV — máx. 10 MB</p>
            </div>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
            />
          </div>

          {/* Formatos suportados */}
          <div className="card space-y-3">
            <h3 className="text-sm font-semibold text-white">Formatos suportados</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-white/60">
              <div className="bg-white/3 rounded-lg p-3">
                <p className="font-medium text-white mb-1">📋 CEI / B3</p>
                <p>Planilha de negociações exportada do Portal do Investidor B3 (cei.b3.com.br)</p>
              </div>
              <div className="bg-white/3 rounded-lg p-3">
                <p className="font-medium text-white mb-1">📄 Template InvestFlow</p>
                <p>Planilha genérica com colunas: Código, Tipo, Data, Quantidade, Preço, Corretora</p>
              </div>
            </div>
            <button onClick={downloadTemplate} className="btn-ghost flex items-center gap-2 text-xs">
              <DocumentArrowDownIcon className="h-4 w-4" />
              Baixar template InvestFlow (.xlsx)
            </button>
          </div>
        </div>
      )}

      {/* STEP 2 — Preview */}
      {step === 'preview' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-white/60">
              <span className="text-white font-semibold">{transactions.length}</span> transações detectadas —{' '}
              <span className="text-primary-400">{selected.size} selecionadas</span>
            </p>
            <div className="flex gap-2">
              <button onClick={() => setSelected(new Set(transactions.map((_, i) => i)))} className="btn-ghost text-xs">
                Selecionar todas
              </button>
              <button onClick={() => setSelected(new Set())} className="btn-ghost text-xs">
                Desmarcar todas
              </button>
            </div>
          </div>

          <div className="card overflow-x-auto p-0">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="p-3 text-left text-white/40 font-medium w-8"></th>
                  <th className="p-3 text-left text-white/40 font-medium">Data</th>
                  <th className="p-3 text-left text-white/40 font-medium">Tipo</th>
                  <th className="p-3 text-left text-white/40 font-medium">Código</th>
                  <th className="p-3 text-right text-white/40 font-medium">Qtd</th>
                  <th className="p-3 text-right text-white/40 font-medium">Preço</th>
                  <th className="p-3 text-right text-white/40 font-medium">Total</th>
                  <th className="p-3 text-left text-white/40 font-medium">Corretora</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx, i) => (
                  <tr
                    key={i}
                    onClick={() => toggleRow(i)}
                    className={`border-b border-white/5 cursor-pointer transition-colors
                      ${selected.has(i) ? 'bg-white/3 hover:bg-white/5' : 'opacity-40 hover:opacity-60'}`}
                  >
                    <td className="p-3">
                      <div className={`h-4 w-4 rounded border flex items-center justify-center
                        ${selected.has(i) ? 'border-primary-400 bg-primary-500/20' : 'border-white/20'}`}>
                        {selected.has(i) && <CheckCircleIcon className="h-3 w-3 text-primary-400" />}
                      </div>
                    </td>
                    <td className="p-3 text-white/70">{new Date(tx.date + 'T12:00:00').toLocaleDateString('pt-BR')}</td>
                    <td className="p-3">
                      <span className={`font-semibold ${tx.type === 'BUY' ? 'text-success-400' : 'text-danger-400'}`}>
                        {tx.type === 'BUY' ? 'Compra' : 'Venda'}
                      </span>
                    </td>
                    <td className="p-3 font-mono font-bold text-white">{tx.code}</td>
                    <td className="p-3 text-right text-white/70">{tx.quantity}</td>
                    <td className="p-3 text-right text-white/70">{hideCurrency(tx.price, isVisible)}</td>
                    <td className="p-3 text-right font-medium text-white">{hideCurrency(tx.totalValue, isVisible)}</td>
                    <td className="p-3 text-white/50">{tx.broker || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <button onClick={() => { setStep('upload'); setTransactions([]); }} className="btn-ghost flex items-center gap-2">
              <XMarkIcon className="h-4 w-4" />
              Cancelar
            </button>
            <button onClick={handleConfirm} disabled={loading || !selected.size} className="btn-primary flex items-center gap-2">
              {loading ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckCircleIcon className="h-4 w-4" />}
              {loading ? 'Importando...' : `Importar ${selected.size} transações`}
            </button>
          </div>
        </div>
      )}

      {/* STEP 3 — Done */}
      {step === 'done' && result && (
        <div className="card flex flex-col items-center py-16 gap-6 text-center">
          <div className="h-16 w-16 rounded-full bg-success-500/15 flex items-center justify-center">
            <CheckCircleIcon className="h-8 w-8 text-success-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Importação concluída!</h2>
            <p className="text-white/50 mt-2">
              <span className="text-white font-semibold">{result.imported}</span> transações importadas
              {result.assetsCreated > 0 && (
                <> · <span className="text-primary-400 font-semibold">{result.assetsCreated}</span> novos ativos criados</>
              )}
            </p>
          </div>
          <div className="flex gap-3">
            <button onClick={() => { setStep('upload'); setTransactions([]); setResult(null); }} className="btn-ghost">
              Nova importação
            </button>
            <a href="/assets" className="btn-primary">Ver meus ativos</a>
          </div>
        </div>
      )}
    </div>
  );
}
